import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FormField, AddressField, SiteShipmentCourierData, Country, ShipmentFormInfo, FormButtonType, ContainerBarcodeInfo } from './service/form-field';
import { ShipmentFormStorage } from './shipment-form-storage';
import { UrlBuilder } from '../../core';
import { GridDetails } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { combineLatest } from 'rxjs';
import { ShipmentFormService } from './service/shipment-form-service';
import { ContainerData, ContainerTransferData, ShipmentData, ShipmentFormData } from '../interfaces/shipment-form-data';
import * as moment from 'moment';
import { ActivityLogger } from '../../activities/activity-logger';
import { uniqBy } from 'lodash';
import { WayBillValidatorData } from '../interfaces/shipment-data';

@Component({
    selector: 'site-shipment-form',
    template: require('./shipment-form.component.html'),
    styles: [require('./shipment-form.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteShipmentFormComponent implements OnChanges {

    @Input()
    public shipmentFormGroup: FormGroup;
    @Input()
    public shipmentData: ShipmentFormInfo;
    @Input()
    public isResumeShipment = false;
    @Input()
    public isDataLoaded = false;
    @Input()
    public shipmentId: number;
    @Output()
    public onFormSubmit: EventEmitter<boolean> = new EventEmitter();
    @Output()
    public formValueChangesEvent: EventEmitter<boolean> = new EventEmitter();
    public shippingFromAddressList: { key: number, value: string, address: AddressField }[] = [];
    public destinationAddressList: { key: number, value: string, address: AddressField }[] = [];
    public customToAddressList: { key: number, value: string, address: AddressField }[] = [];
    public courierList: { key: number, value: string, isPreferred: boolean, lgxCourierId?: number }[] = [];
    public countryList: Country[] = [];
    public hideSelectField = false;
    public showBarcodeScanner = false;
    public formFields: FormField[] = [];
    public showShippingFromLoading = false;
    public showDestinationLoading = false;
    public showCourierLoading = false;
    public gridDetails: GridDetails = {
        header: this.createShipmentFormService.getShipmentTableHeader(),
        data: []
    };
    public selectedAddress: AddressField = new AddressField();
    public clearData = true;
    public showNotSavedAlert = true;
    public showBarcodeLoading = false;
    public showAirbillLoading = false;
    public showSubmitLoading = false;
    public showSaveLoading = false;
    public errorMessage = '';
    public showShipmentDetails = false;
    public shippingLabel = 'Central Labs Location';
    public shippingToList: { key: number, value: string, address: AddressField }[];
    public waybillValidatorData: WayBillValidatorData[] = [];
    public airbillDisclaimer = 'Disclaimer - Please submit one waybill number per shipment submission';
    private containerBarcodes: string[] = [];
    private searchedBarcodes: string;
    private airbillExists = false;
    private selectedCourier: { key: number, value: string, isPreferred: boolean, lgxCourierId?: number };
    private validAirbillFormat = true;
    private containerData: ContainerData[] = [];
    private containerTransferData: ContainerTransferData[] = [];
    private containerTransferReason: { description: string, transferReasonId: number }[] = [];
    private containerCreationStartedOn = '';
    private isEmulated: boolean;

    constructor(
        private storage: ShipmentFormStorage,
        private urlBuilder: UrlBuilder,
        private changeDetecterRef: ChangeDetectorRef,
        private createShipmentFormService: ShipmentFormService,
        activityLogger: ActivityLogger,
    ) {
        moment.locale('en');
        this.containerCreationStartedOn = moment().local().format('DD-MMM-YY hh:mm:ss A Z');
        this.loadCustomAddressAndTransferReason();
        this.loadCountryList();
        this.isEmulated = this.createShipmentFormService.isUserEmulated();
    }

    public ngOnChanges(simpleChange: SimpleChanges) {
        if (simpleChange.isDataLoaded && this.isDataLoaded) {
            this.loadFormData();
            this.loadDestinationAddressOnOrgName();
            this.onFormValueChanges();
        } 
    }

    @ViewChild('shipmentDetails')
    set shipmentDetails(val: ElementRef<HTMLDivElement>) {
        if (!val) { return; }
        document.getElementById('shipmentDetails').scrollIntoView();
    }

    public next() {
        this.showShipmentDetails = true;
        if (this.searchedBarcodes !== this.containerBarcodes.toString()) {
            this.onContainerBarcodeChanges(this.containerBarcodes);
        }
        this.createShipmentFormService.setCurrentFocusData(true);
    }

    public onBarcodeAddedEvent($event: ContainerBarcodeInfo) {
        this.containerBarcodes = $event.barcodes;
        this.containerTransferData = $event.containerTransferData;
        this.containerData = $event.containerData;
    }

    public async onContainerHandlerButtonClick($event: FormButtonType) {
        if ($event === FormButtonType.Next) {
            this.next();
        }
        if ($event === FormButtonType.Save) {
            await this.saveForLater();
        }
        if ($event === FormButtonType.Cancel) {
            this.cancel();
        }
        if ($event === FormButtonType.Edit) {
            this.editBarcodes();
        }
    }

    public editBarcodes() {
        this.showShipmentDetails = false;
        this.createShipmentFormService.setCurrentFocusData(false);
    }

    public back() {
        this.showShipmentDetails = false;
        this.createShipmentFormService.setCurrentFocusData(false);
    }

    public getFormControl(name: string) {
        return this.shipmentFormGroup.get(name) as FormControl;
    }

    public async onSubmit(isSaveAction = false, orgName = '') {
        let result = await this.createShipmentFormService.showSaveOrSubmitConfirmation(orgName, isSaveAction).toPromise();
        if (result) { return; }
        const shipmentformData = this.constructShipmentData(this.shipmentFormGroup.value, isSaveAction);
        this.showNotSavedAlert = false;
        this.showSubmitLoading = !isSaveAction;
        this.showSaveLoading = isSaveAction;
        this.storage.createShipment(shipmentformData)
            .subscribe((res: any) => {
                this.showSubmitLoading = false;
                this.showSaveLoading = false;
                this.removeLocalStorage();
                this.onFormSubmit.emit(true);
                if (isSaveAction || this.isEmulated) {
                    this.urlBuilder.esa().sampleManagement().siteShipping().inProgressShipmentList().navigate();
                } else {
                    this.urlBuilder.esa().sampleManagement().siteShipping().shipmentSubmittedSummary(res.shipmentId).navigate();
                }
            });
    }

    public async saveForLater() {
        let orgName = this.shipmentFormGroup.get('destinationAddress').get('destinationOrgName').value;
        if (!this.containerBarcodes || this.containerBarcodes.length === 0 || !orgName) {
            let result = await this.createShipmentFormService.showSaveShipmentError().toPromise();
            if (result.isCancelled) { return; }
            orgName = result.selectedOption.confirmOptions;
            this.createShipmentFormService.patchShipmentName(orgName, this.shipmentFormGroup);
        }
        await this.onSubmit(true, orgName);
    }

    public cancel() {
        this.urlBuilder.esa().sampleManagement().siteShipping().inProgressShipmentList().navigate();
    }

    public isFormInValid() {
        return !this.shipmentFormGroup.valid || this.airbillExists || !this.validAirbillFormat || !this.containerBarcodes || this.containerBarcodes.length === 0;
    }

    public isAirbillNumberValid($event: string) {
        if (!$event) { return; }
        this.showAirbillLoading = true;
        let courierName = this.getFormControl('courier').value;
        if (courierName && !this.selectedCourier) {
            this.selectedCourier = this.courierList.find(cl => cl.value === courierName);
        }
        this.storage.isAribillNumberValid($event, courierName).subscribe({
            next: (result: any) => {
                this.waybillValidatorData = result.courierData;
                this.showAirbillLoading = false;
                this.airbillExists = result.isValid;
                this.changeDetecterRef.markForCheck();
            },
            error: (error) => {
                this.waybillValidatorData = null;
                this.showAirbillLoading = false;
                this.airbillExists = false;
                this.changeDetecterRef.markForCheck();
            }
        });
        //this.validateAirBillFormat();
    }

    public validateCourier($event: any) {
        this.selectedCourier = $event;
        this.airbillExists = this.storage.validateCourier(this.selectedCourier.value, this.waybillValidatorData);
        //this.validateAirBillFormat();
        this.changeDetecterRef.markForCheck();
    }

    public validateAirBillFormat() {
        let airbillNumber = this.shipmentFormGroup.get('airBillNumber').value;
        if (this.airbillExists || !this.selectedCourier || !airbillNumber) { return; }
        this.storage.validateAirbillNumber(airbillNumber, this.selectedCourier).subscribe((result) => {
            this.validAirbillFormat = result;
        });
    }

    public getWayBillError() {
        let airbillControl = this.getFormControl('airBillNumber');
        if (!airbillControl.valid) { return ''; }
        if (this.airbillExists) {
            return 'Waybill Number entered has been used in another shipment.';
        }
        if (!this.validAirbillFormat) {
            return 'Waybill number is not a known format for this courier, please confirm number is correct';
        }
        return ''; 
    }

    public removeLocalStorage() {
        this.createShipmentFormService.deleteLocalStorage();
        this.containerData = [];
        this.containerTransferData = [];
    }

    public onShippingToLabcorpChange($event: boolean) {
        this.hideSelectField = !$event;
        this.shippingToList = this.hideSelectField ? this.customToAddressList : this.destinationAddressList;
        this.shippingLabel = $event ? 'Central Labs Location' : 'Shipping To';
    }

    private loadCourierData(courierList: SiteShipmentCourierData[]) {
        if (!courierList || !courierList.length) { return []; }
        courierList = courierList.filter(t => t.courierName && t.courierName.length);
        let uniqueCourierList: { key: number; value: string, isPreferred: boolean, lgxCourierId?: number }[] = courierList.
            map((item) => {
                return {
                    key: item.labSiteCourierId,
                    value: item.courierName,
                    isPreferred: item.isPreferred,
                    lgxCourierId: item.lgxCourierId
                };
            }).
            filter((data, i, arr) => arr.findIndex(t => t.key === data.key) === i).sort((a, b) => a > b ? -1 : 1);
        let preferredCourier = uniqueCourierList.filter(t => t.isPreferred);
        if (preferredCourier && preferredCourier.length === 1) {
            this.selectedCourier = preferredCourier[0];
            this.shipmentFormGroup.patchValue({ 'courier': this.selectedCourier.value });
        }
        return uniqueCourierList;
    }

    private onContainerBarcodeChanges(barcodes: string[]) {
        if (!barcodes || barcodes.length === 0) { return; }
        var formattedBarcodes = this.createShipmentFormService.formatBarcodeList(barcodes);
        this.searchedBarcodes = barcodes.toString();
        this.loadShippingFromAddress(formattedBarcodes);
        this.loadDestinationAddress(formattedBarcodes);
        this.loadCourierList(formattedBarcodes);
    }

    private loadFormData() {
        if (!this.shipmentData) { return; }
        this.containerCreationStartedOn = this.shipmentData.containerCreationStartedOn;
        if (this.shipmentData.formValue) {
            this.clearData = false;
        }
        if (this.shipmentFormGroup.get('shippingToLabcorp').value === 'no') { this.onShippingToLabcorpChange(false); }
        if (this.shipmentData.barcodes) {
            this.containerBarcodes = this.shipmentData.barcodes;
        }
        if (this.shipmentData.gridData) {
            this.gridDetails = this.shipmentData.gridData;
            this.containerData = this.shipmentData.containerData;
        }
        if (this.shipmentData.transferData) {
            this.containerTransferData = this.shipmentData.transferData;
        }
        this.showShipmentDetails = this.shipmentData.currentFocus;
        if (this.showShipmentDetails || (this.isResumeShipment && this.containerBarcodes.length > 0)) {
            this.onContainerBarcodeChanges(this.containerBarcodes);
        }
        if (this.isResumeShipment) {
            this.createShipmentFormService.setResumeShipment({ isResumeShipment: this.isResumeShipment });
        }
        this.changeDetecterRef.markForCheck();
        //this.clearData = true;
    }

    private loadToLabcorpAddress(addressList: { key: number, value: string, address: AddressField }[]) {
        return addressList.
            filter((data, i, arr) => arr.
                findIndex(t => t.value.toLowerCase() === data.value.toLowerCase()) === i);
    }

    private loadDestinationAddressOnOrgName() {
        this.shipmentFormGroup.get('destinationAddress').get('destinationOrgName').valueChanges.subscribe((name: string) => {
            if (name && name.length >= 3 && this.hideSelectField) {
                if (this.customToAddressList.length !== 0) {
                    let addressField = this.customToAddressList.filter(t => t.address.companyName &&
                        t.address.companyName.toLowerCase() === name.toLowerCase()).map((t => t.address));
                    if (addressField && addressField.length > 0) {
                        this.selectedAddress = addressField[0];
                    } else {
                        this.selectedAddress = this.customToAddressList.filter(t => t.value === 'Others')[0].address;
                    }
                }
            }
        });
    }

    private constructShipmentData(shipmentFormGroup: any, isSaveAction = false): ShipmentFormData {
        let shipmentData: ShipmentData = {} as ShipmentData;
        if (shipmentFormGroup) {
            if (shipmentFormGroup.shippingFromAddress) {
                shipmentData = this.createShipmentFormService.loadShippingFromAddressDetails(shipmentFormGroup, shipmentData, this.countryList);
            }
            if (shipmentFormGroup.destinationAddress) {
                shipmentData = this.createShipmentFormService.loadDestinationAddressDetails(shipmentFormGroup, shipmentData, this.countryList, this.destinationAddressList);
            }
            const selectedCourier = this.courierList.find(cl => cl.value === shipmentFormGroup.courier);
            if (selectedCourier) {
                shipmentData.labsiteCourierId = selectedCourier.key;
            }
            moment.locale('en');
            shipmentData.createdOn = this.containerCreationStartedOn;
            shipmentData.trackingNumber = shipmentFormGroup.airBillNumber;
            shipmentData.submittedOn = isSaveAction ? null : moment().local().format('DD-MMM-YY hh:mm:ss A Z');
            shipmentData.scheduledShipmentDate = shipmentFormGroup.scheduledShipmentDate;
        }
        const returnData = { shipmentData, containerData: this.containerData, isInProgressShipment: this.isResumeShipment, isEmulated: this.isEmulated } as ShipmentFormData;
        if (this.isResumeShipment) {
            returnData.shipmentId = this.shipmentData.shipmentId;
        }
        if (this.containerTransferData) {
            returnData.containerTransferData = this.containerTransferData;
        }
        return returnData;
    }

    private loadCustomAddressAndTransferReason() {
        combineLatest([this.storage.getDestinationAddress(), this.storage.getContainerTransferReason()])
            .subscribe(([addressList, reasonList]) => {
                this.customToAddressList = addressList;
                this.shippingToList = this.hideSelectField ? this.customToAddressList : this.destinationAddressList;
                this.containerTransferReason = reasonList.transferData;
            });
    }

    private loadCountryList() {
        this.storage.loadCountryList().subscribe(result => {
            this.countryList = result;
            this.changeDetecterRef.markForCheck();
        });
    }

    private onFormValueChanges() {
        this.shipmentFormGroup.get('airBillNumber').valueChanges.subscribe((value: string) => {
            value = value.trim();
            if (value && value.length >= 9) {
                this.isAirbillNumberValid(value);
            }
        });
        this.shipmentFormGroup.valueChanges.subscribe(() => {
            this.setFormValuesToLocalStorage();
            this.formValueChangesEvent.emit(true);
        });
    }

    private setFormValuesToLocalStorage() {
        const shipmentFormData = this.shipmentFormGroup.value;
        shipmentFormData.createdOn = this.containerCreationStartedOn;
        this.createShipmentFormService.setFormValuesToLocalStorage(shipmentFormData);
    }

    private loadShippingFromAddress(barcodes: string[]) {
        this.showShippingFromLoading = true;
        this.storage.getShippingFromAddress(barcodes).subscribe((shippingFromAddress) => {
            this.shippingFromAddressList = shippingFromAddress;
            this.shippingFromAddressList.sort((a, b) => a.value.localeCompare(b.value));
            this.shippingFromAddressList.push(this.shippingFromAddressList.splice(this.shippingFromAddressList.findIndex(a => a.value === 'Enter Custom Address'), 1)[0]);
            this.showShippingFromLoading = false;
            this.changeDetecterRef.markForCheck();
        });
    }

    private loadDestinationAddress(barcodes: string[]) {
        this.showDestinationLoading = true;
        this.storage.getToLabcorpAddress(barcodes).subscribe((toLabcorpAddress) => {
            this.destinationAddressList = this.loadToLabcorpAddress(toLabcorpAddress).sort((a, b) => a.value.localeCompare(b.value));
            this.destinationAddressList.push(this.destinationAddressList.splice(this.destinationAddressList.findIndex(a => a.value === 'Enter Custom Address'), 1)[0]);
            this.shippingToList = this.hideSelectField ? this.customToAddressList : this.destinationAddressList;
            this.showDestinationLoading = false;
            this.changeDetecterRef.markForCheck();
        });
    }

    private loadCourierList(barcodes: string[]) {
        this.showCourierLoading = true;
        this.storage.getCourier(barcodes).subscribe((courierList) => {
            this.courierList = this.loadCourierData(courierList).sort((a, b) => a.value.localeCompare(b.value));
            this.showCourierLoading = false;
            this.changeDetecterRef.markForCheck();
        });
    }
}